import { useLiveQuery } from "@tanstack/react-db";
import { createContext, useContext, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { currentFeedPendingInvitesQuery, currentFeedQuery, myFeedPermissionsQuery } from "@/models/repositories";
import { store } from "@/store/store";
import type { DirectWsInvitation, Feed } from "@/store/types";
import accountInfo, { type AccountInfo } from "../accountInfo";
import { WorkspaceContext } from "./workspaceProvider";

type FeedState = {
  currentFeedId?: string;
  currentFeed?: Feed;
  currentFeedAccounts?: Map<string, AccountInfo>;
  currentFeedPendingInvites?: DirectWsInvitation[];
  isCurrentFeedAdmin?: boolean;
  haveWritePermission?: boolean;
};

export const CurrentFeedContext = createContext<FeedState>({});

const CurrentFeedProvider = ({ children }) => {
  const { feedId: currentFeedId } = useParams();
  const { myCurrentWorkspaceMembership } = useContext(WorkspaceContext);

  const feedAccounts = [];

  const { data: currentFeeds } = useLiveQuery((q) => (currentFeedId ? currentFeedQuery(currentFeedId)(q) : null));
  const currentFeed = currentFeeds?.[0];

  const currentFeedAccounts = useMemo(() => accountInfo(feedAccounts), [feedAccounts]);

  const myMembershipId = myCurrentWorkspaceMembership?.id;
  const { data: myFeedPermissionRows } = useLiveQuery((q) =>
    currentFeedId && myMembershipId ? myFeedPermissionsQuery(currentFeedId, myMembershipId)(q) : null,
  );
  const myFeedPermissions = myFeedPermissionRows ?? [];

  const isCurrentFeedAdmin = useMemo(() => myFeedPermissions?.some((p) => p.name === "admin"), [myFeedPermissions]);
  const haveWritePermission = useMemo(() => myFeedPermissions?.some((p) => p.name === "write"), [myFeedPermissions]);

  const { data: pendingInviteRows } = useLiveQuery((q) =>
    currentFeedId ? currentFeedPendingInvitesQuery(currentFeedId)(q) : null,
  );
  const currentFeedPendingInvites: DirectWsInvitation[] = pendingInviteRows ?? [];

  useEffect(() => {
    if (currentFeedId) {
      store.update("feed", currentFeedId, (row) => ({ ...row, lastOpened: new Date().toISOString() }));
    }
  }, [currentFeedId]);

  return (
    <CurrentFeedContext.Provider
      value={{
        currentFeedId,
        currentFeed,
        currentFeedAccounts,
        currentFeedPendingInvites,
        isCurrentFeedAdmin,
        haveWritePermission,
      }}
    >
      {children}
    </CurrentFeedContext.Provider>
  );
};

export default CurrentFeedProvider;
